import { handleErrorResponse } from '@frontend/api-client/utils';
import { match } from '@frontend/duck-tape';
import { accountCreationSchemaLegacyDefaultValues, useAccountCreationFormLegacy, validators } from '@frontend/forms';
import { useEffect, useFeatureIsOn, useMemo } from '@frontend/react';
import { useDefaultPromoCode, usePromotionTranslation } from '@frontend/react-domain';
import { AppShell, LoadingSpinnerPanel, useBreakpoints } from '@frontend/web-react';
import { AppContentWrapper, SignUpLanding, UnauthenticatedAppHeader } from '@frontend/web/components';
import { CreatePassword, UserInfo } from '@frontend/web/components/DomainSpecific/AcceptInvite';
import { GenericErrorPage } from '@frontend/web/errorPages';
import {
  useAnalytics,
  useLazyRetrievePromotionDetailsQuery,
  useLazyRetrieveReferrerDetailsQuery,
  useRetrieveClientQuery,
  useRetrieveInviteQuery,
} from '@frontend/web/hooks';
import { useSignUpFlow } from '../../hooks/useSignUpFlow';
import { useStepNavigation } from '../../hooks/useStepNavigation';
import {
  AUTHENTICATED_HOME_ROUTE,
  createFileRoute,
  getInviteErrorStateFromError,
  handleMutation,
  redirect,
} from '../../utils';

type AcceptInviteSearch = { promo?: string };

const getInviteFailureTrackingData = (error: unknown) => {
  const errorRecord = typeof error === 'object' && error !== null ? (error as Record<string, unknown>) : undefined;
  const errorData =
    typeof errorRecord?.data === 'object' && errorRecord.data !== null
      ? (errorRecord.data as Record<string, unknown>)
      : undefined;
  const backendCode = typeof errorData?.code === 'number' ? errorData.code : undefined;
  const httpStatus = typeof errorRecord?.status === 'number' ? errorRecord.status : undefined;

  return {
    backendCode,
    errorCategory: 'network' as const,
    errorCode: backendCode ? `INVITE_${backendCode}` : 'INVITE_RETRIEVE_FAILED',
    errorSource: 'inviteRetrieve',
    httpStatus,
    retriable: true,
  };
};

const signUpSteps = ['userInfo', 'createPassword'] as const;

/**
 * Legacy accept invite wizard component (flag OFF)
 * Uses the two-step wizard flow: UserInfo → CreatePassword
 * Collects birthday and phone during signup with pre-filled invite data
 */
const LegacyAcceptInviteWizard = () => {
  const { inviteId } = Route.useParams();
  const { promo } = Route.useSearch() as AcceptInviteSearch;
  const navigate = Route.useNavigate();
  const { isMobile, isTablet } = useBreakpoints();
  const { track } = useAnalytics();
  const { currentStep, decrementStep, incrementStep, setCurrentStep } = useStepNavigation(signUpSteps, 'userInfo');

  const {
    data: inviteData,
    error: retrieveInviteError,
    isLoading: isRetrieveInviteLoading,
  } = useRetrieveInviteQuery(inviteId);

  const inviteErrorState = useMemo(() => getInviteErrorStateFromError(retrieveInviteError), [retrieveInviteError]);

  const searchParams = { inviteId, promo };

  const { handleSubmitForm, isAnyLoading: isSignUpFlowLoading } = useSignUpFlow({
    inviteData,
    navigateOnSuccess: navigate,
    onErrorSetStep: setCurrentStep,
    searchParams,
    type: 'accept-invite',
  });

  const retrieveClientHookResult = useRetrieveClientQuery();
  const retrievePromotionDetailsHookResult = useLazyRetrievePromotionDetailsQuery();

  const defaultPromoCode = useDefaultPromoCode({
    overridePromoCode: promo,
    retrieveClientHookResult,
  });

  const { actions: promotionTranslationActions, state: promotionTranslationState } = usePromotionTranslation({
    defaultPromoCode,
    handleMutation,
    retrievePromotionDetailsHookResult,
  });

  const form = useAccountCreationFormLegacy({
    initialValues: accountCreationSchemaLegacyDefaultValues,
    onSubmit: handleSubmitForm,
  });

  const { setFieldValue } = form;

  // Handle invite error
  useEffect(() => {
    if (retrieveInviteError) {
      handleErrorResponse(retrieveInviteError, (error) => {
        track({
          data: {
            ...getInviteFailureTrackingData(error),
            entryType: 'accept-invite',
            flowVariant: 'legacy',
            hasPromoCode: Boolean(promo),
            platform: 'web',
          },
          event: 'signupSubmitFailed',
          topic: 'auth',
        });
      });
    }
  }, [promo, retrieveInviteError, track]);

  // Pre-fill form with invite data
  useEffect(() => {
    if (inviteData) {
      setFieldValue('email', inviteData.email || '');
      setFieldValue('firstName', inviteData.firstName || '');
      setFieldValue('lastName', inviteData.lastName || '');
      setFieldValue('phoneNumber', inviteData.phoneNumber || '');
      setFieldValue('nickname', inviteData.nickname || '');
      setFieldValue('birthday', inviteData.birthday || ('' as DateISOString));
    }
  }, [inviteData, setFieldValue]);

  // Set promo code
  useEffect(() => {
    setFieldValue('promoCode', promotionTranslationState.promoCode ?? '');
  }, [promotionTranslationState.promoCode, setFieldValue]);

  useEffect(() => {
    track({
      data: {
        entryType: 'accept-invite',
        flowVariant: 'legacy',
        platform: 'web',
        step: currentStep,
      },
      event: 'signupStepViewed',
      topic: 'auth',
    });
  }, [currentStep, track]);

  const hasInviteError = Boolean(retrieveInviteError);
  const shouldRenderInviteErrorInline = hasInviteError && inviteErrorState.renderMode === 'inline';
  const inviteErrorMessage = inviteErrorState.description || 'We could not load your invite. Please try again.';
  const isAnyLoading = isRetrieveInviteLoading || isSignUpFlowLoading;

  const renderContent = () => {
    if (isRetrieveInviteLoading) {
      return <LoadingSpinnerPanel fullScreen />;
    }

    if (hasInviteError && !shouldRenderInviteErrorInline) {
      return <GenericErrorPage giantText="Oops," subtitle={inviteErrorMessage} title="" />;
    }

    return match(currentStep)
      .with('userInfo', () => (
        <UserInfo
          disabledFields={{
            email: !!inviteData?.email,
            phoneNumber: !!inviteData?.phoneNumber,
          }}
          errorBannerMessage={shouldRenderInviteErrorInline ? inviteErrorMessage : undefined}
          form={form}
          incrementStep={incrementStep}
          inviteDetails={inviteData}
          isAnyLoading={isAnyLoading}
        />
      ))
      .with('createPassword', () => (
        <CreatePassword
          decrementStep={decrementStep}
          form={form}
          inviteDetails={inviteData}
          isAnyLoading={isAnyLoading}
          promotionTranslationActions={promotionTranslationActions}
          promotionTranslationState={promotionTranslationState}
          searchParamsDescription={undefined}
          searchParamsPromoCode={promo}
        />
      ))
      .exhaustive();
  };

  return (
    <AppShell>
      <UnauthenticatedAppHeader isHidden={isMobile || isTablet} />
      <AppContentWrapper className="flex-1">{renderContent()}</AppContentWrapper>
    </AppShell>
  );
};

/**
 * New accept invite flow component (flag ON)
 * Uses SignUpLanding with SSO options and invite data
 */
const NewAcceptInviteFlow = () => {
  const { inviteId } = Route.useParams();
  const { promo } = Route.useSearch() as AcceptInviteSearch;
  const { isMobile, isTablet } = useBreakpoints();
  const { track } = useAnalytics();

  const {
    data: inviteData,
    error: retrieveInviteError,
    isLoading: isRetrieveInviteLoading,
  } = useRetrieveInviteQuery(inviteId);

  const [retrieveReferrerData, { data: referrerData, isLoading: isReferrerDetailsLoading }] =
    useLazyRetrieveReferrerDetailsQuery();
  const inviteErrorState = useMemo(() => getInviteErrorStateFromError(retrieveInviteError), [retrieveInviteError]);

  useEffect(() => {
    if (inviteData?.invitedBy) {
      retrieveReferrerData(inviteData.invitedBy);
    }
  }, [inviteData, retrieveReferrerData]);

  useEffect(() => {
    if (retrieveInviteError) {
      handleErrorResponse(retrieveInviteError, (error) => {
        track({
          data: {
            ...getInviteFailureTrackingData(error),
            entryType: 'accept-invite',
            flowVariant: 'v2',
            hasPromoCode: Boolean(promo),
            platform: 'web',
          },
          event: 'signupSubmitFailed',
          topic: 'auth',
        });
      });
    }
  }, [promo, retrieveInviteError, track]);

  const isAnyLoading = isRetrieveInviteLoading || isReferrerDetailsLoading;
  const hasInviteError = Boolean(retrieveInviteError);
  const shouldRenderInviteErrorInline = hasInviteError && inviteErrorState.renderMode === 'inline';
  const inviteErrorMessage = inviteErrorState.description || 'We could not load your invite. Please try again.';

  const searchParams = {
    inviteId,
    promo,
  };

  const renderContent = () => {
    if (isAnyLoading) {
      return <LoadingSpinnerPanel fullScreen />;
    }

    if (hasInviteError && !shouldRenderInviteErrorInline) {
      return <GenericErrorPage giantText="Oops," subtitle={inviteErrorMessage} title="" />;
    }

    if (shouldRenderInviteErrorInline || inviteData) {
      return (
        <SignUpLanding
          disableSignUpActions={shouldRenderInviteErrorInline}
          inviteData={inviteData}
          inviteErrorMessage={shouldRenderInviteErrorInline ? inviteErrorMessage : undefined}
          referrerName={referrerData?.preferredName}
          searchParams={searchParams}
        />
      );
    }

    return null;
  };

  return (
    <AppShell>
      <UnauthenticatedAppHeader isHidden={isMobile || isTablet} />
      <AppContentWrapper className="flex-1">{renderContent()}</AppContentWrapper>
    </AppShell>
  );
};

/**
 * Accept invite route component with feature flag support.
 *
 * When is_signup_changes_enabled is OFF (default):
 * - Renders legacy two-step wizard (UserInfo → CreatePassword)
 * - Email is pre-filled and disabled from invite
 * - Collects birthday and phone during signup
 * - Password requires 12+ characters
 *
 * When is_signup_changes_enabled is ON:
 * - Renders SignUpLanding with SSO options and invite context
 * - Defers birthday/phone to post-signup onboarding
 * - Password requires 8+ chars with 1 number
 */
const AcceptInvite = () => {
  const { enabled: isSignupChangesEnabled } = useFeatureIsOn('is_signup_changes_enabled');

  if (isSignupChangesEnabled) {
    return <NewAcceptInviteFlow />;
  }

  return <LegacyAcceptInviteWizard />;
};

export const Route = createFileRoute('/accept-invite/$inviteId')({
  beforeLoad: async ({ context }) => {
    if (context.authentication?.isAuthenticated) {
      throw redirect({ to: AUTHENTICATED_HOME_ROUTE });
    }
  },
  component: AcceptInvite,
  validateSearch: (search) =>
    validators
      .object({
        promo: validators.optionalString(),
      })
      .parse(search),
});
